/**
 * useSurveySessions — lista de sesiones de relevamiento de un proyecto
 *
 * @remarks
 * Carga las sesiones al montar y expone reload para refrescar.
 * Patrón manual idéntico a useUrbanEntities.
 *
 * @module survey/hooks
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { getSurveyApiError, surveyApi } from '../services/survey-api.service';
import type { SurveySession } from '../types/survey.types';

interface UseSurveySessionsOptions {
  projectId: string;
  autoLoad?: boolean;
}

export interface UseSurveySessionsResult {
  sessions: SurveySession[];
  isLoading: boolean;
  error: string | null;
  reload: () => Promise<void>;
}

export function useSurveySessions({
  projectId,
  autoLoad = true,
}: UseSurveySessionsOptions): UseSurveySessionsResult {
  const [sessions, setSessions] = useState<SurveySession[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const loadingRef = useRef<string | null>(null);

  const load = useCallback(async () => {
    if (!projectId) return;

    loadingRef.current = projectId;
    setIsLoading(true);
    setError(null);

    try {
      const data = await surveyApi.listSessions(projectId);
      if (loadingRef.current === projectId) setSessions(data);
    } catch (err) {
      if (loadingRef.current === projectId) setError(getSurveyApiError(err));
    } finally {
      if (loadingRef.current === projectId) setIsLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    if (autoLoad && projectId) void load();
  }, [autoLoad, projectId, load]);

  return { sessions, isLoading, error, reload: load };
}
